/* import __COLOCATED_TEMPLATE__ from './suggestion-summary-card.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import type AppService from 'embercom/services/app-service';
import type Store from '@ember-data/store';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { taskFor } from 'ember-concurrency-ts';
import type IntlService from 'ember-intl/services/intl';
import { CAN_REPORTING_DRILLIN_ACCESS } from 'embercom/lib/knowledge-hub/constants';
import type ContentReviewRequest from 'embercom/models/content-service/content-review-request';
import { ReviewType } from 'embercom/models/content-service/content-review-request';
import { Status } from 'embercom/models/data/content-service/content-review-statuses';
import type SuggestionService from 'embercom/services/suggestion-service';
import { type ReviewableContent } from 'embercom/services/suggestion-service';
import { tracked } from '@glimmer/tracking';
import type { Tab } from 'embercom/components/reporting/automation/ai-insights/suggestion-explore-panel';
import { trackedTask } from 'ember-resources/util/ember-concurrency';
import { restartableTask } from 'ember-concurrency-decorators';
import type { TaskGenerator } from 'ember-concurrency';
import type { RelatedContentEntity } from 'embercom/services/suggestion-service';
import { EntityType } from 'embercom/models/data/entity-types';
import type { EntityIdentifierTuple } from 'embercom/services/content-suggestions-service';
import numberFormatter from 'embercom/lib/number-formatter';
import type ContentSection from 'embercom/models/content-service/content-section';
import type ContentStatistic from 'embercom/models/outbound/content-statistic';
import type ContentSectionRelationship from 'embercom/models/content-service/content-section-relationship';
import {
  replaceContentPlaceholders,
  getDuplicateReviewBaseReason,
  getDuplicateReviewBulletPoints,
} from 'embercom/components/knowledge-hub/content-editor/shared/utils/side-by-side-review-helper';

interface Args {
  pendingEditsCount: number;
  reviewRequest: ContentReviewRequest;
  onGoToFirstSuggestion: () => void;
  onStatusChange: (status: Status, contentToLoad?: EntityIdentifierTuple) => void;
  activeContent: ReviewableContent;
  getAiStatsForSection?: (section: ContentSection) => ContentStatistic[];
  handleCondupeSuggestion?: (action: 'accept' | 'reject') => void;
  fromConDupeSuggestion?: boolean;
}

export default class SuggestionSummaryCard extends Component<Args> {
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare suggestionService: SuggestionService;
  @service declare permissionsService: $TSFixMe;
  @service declare appService: AppService;
  @service declare intercomEventService: $TSFixMe;

  @tracked showSidePanel = false;
  @tracked activeTab: Tab = 'related_content';
  @tracked loadedRelatedEntities: RelatedContentEntity[] = [];

  constructor(owner: any, args: Args) {
    super(owner, args);
    if (args.reviewRequest && !this.isConDupeSuggestion) {
      this.fetchRelatedContentTask.perform(args.reviewRequest);
    }
  }

  fetchRelatedContentTask = taskFor(this.loadRelatedContent);
  relatedContentData = trackedTask(this, this.fetchRelatedContentTask, () => [
    this.args.reviewRequest,
  ]);

  get reviewRequestCreatedAt() {
    if (this.args.reviewRequest) {
      return this.args.reviewRequest.get('createdAt');
    }
    return '';
  }

  get isStandaloneApp() {
    return this.appService.app.isStandaloneApp;
  }

  get isEditReviewType() {
    return this.args.reviewRequest?.get('reviewType') === ReviewType.EDIT_CONTENT;
  }

  get isUnderperformingContent() {
    return this.args.reviewRequest?.isUnderperformingContent;
  }

  get isContradictingSuggestion() {
    return this.args.reviewRequest?.isContradictingContent;
  }

  get isDuplicateSuggestion() {
    return this.args.reviewRequest?.isDuplicateContent;
  }

  get isConDupeSuggestion() {
    return this.isContradictingSuggestion || this.isDuplicateSuggestion;
  }

  get isFromConDupeSuggestion() {
    return this.args.fromConDupeSuggestion && this.isConDupeSuggestion;
  }

  get genericConDupeReviewMessage() {
    if (!this.isFromConDupeSuggestion) {
      return '';
    }

    if (this.isContradictingSuggestion) {
      return this.intl.t(
        'knowledge-hub.content-editor.review.suggestion-summary-card.contradiction-generic-message',
      );
    } else if (this.isDuplicateSuggestion) {
      return this.intl.t(
        'knowledge-hub.content-editor.review.suggestion-summary-card.duplication-generic-message',
      );
    }

    return '';
  }

  get involvedConversationsCount() {
    return numberFormatter(
      this.args.reviewRequest?.get('suggestion')?.numberOfInvolvedConversations ?? 0,
    );
  }

  get resolvedConversationsCount() {
    return numberFormatter(
      this.args.reviewRequest?.get('suggestion')?.numberOfResolvedConversations ?? 0,
    );
  }

  get contentResolutionRate() {
    return this.args.reviewRequest?.get('suggestion')?.resolutionRate ?? 0;
  }

  get appResolutionRate() {
    return this.args.reviewRequest?.get('suggestion')?.appResolutionRate ?? 0;
  }

  get resolutionRateDifference() {
    return Math.abs(this.args.reviewRequest?.get('suggestion')?.resolutionRateDifference ?? 0);
  }

  get isSaving() {
    return this.isApproving || this.isRejecting;
  }

  get isApproving() {
    return taskFor(this.suggestionService.approveSuggestion).isRunning;
  }

  get isRejecting() {
    return taskFor(this.suggestionService.rejectSuggestion).isRunning;
  }

  get relatedContentCount() {
    return this.loadedRelatedEntities.length;
  }

  get allEditsHaveBeenActioned() {
    return this.args.pendingEditsCount === 0;
  }

  get hasSuggestionMetadata() {
    return isPresent(this.args.reviewRequest?.get('suggestion'));
  }

  get canShowMoveContentSelect() {
    if (!this.hasSuggestionMetadata) {
      return false;
    }

    return this.isEditReviewType || this.isStandaloneApp;
  }

  @restartableTask
  *loadRelatedContent(reviewRequest: ContentReviewRequest): TaskGenerator<void> {
    if (reviewRequest) {
      this.loadedRelatedEntities = yield this.suggestionService.loadRelatedContent(
        reviewRequest.get('id'),
      );
    }
  }

  @action
  async openSidePanel(activeTab: Tab = 'related_content') {
    try {
      await this.permissionsService.checkPermission(CAN_REPORTING_DRILLIN_ACCESS);
    } catch (e) {
      return;
    }
    let suggestion = this.args.reviewRequest?.get('suggestion');
    if (suggestion) {
      let conversationIds = this.isUnderperformingContent
        ? suggestion.unresolvedConversationIds
        : suggestion.includedConversationIds;

      // Only open side panel if there are conversations to show
      if (conversationIds && conversationIds.length > 0) {
        this.activeTab = activeTab;
        this.showSidePanel = true;
      }
    }
  }

  @action
  trackAnalyticsEvent(action: string, object: string, extraContext: Record<string, any> = {}) {
    this.intercomEventService.trackAnalyticsEvent({
      place: 'analyze/optimize-fin',
      action,
      object,
      ...extraContext,
    });
  }

  @action
  async handleSuggestion(
    reviewRequest: ContentReviewRequest,
    acceptSuggestion: boolean,
    entityTypeToApproveAs?: EntityType,
  ) {
    if (acceptSuggestion) {
      let approvedAsEntityType = entityTypeToApproveAs ?? EntityType.ContentSnippet; // An Add content suggestion is by default a snippet so we can safely default to that
      await taskFor(this.suggestionService.approveSuggestion).perform(
        reviewRequest,
        approvedAsEntityType,
      );
      if (approvedAsEntityType === EntityType.ArticleContent) {
        // reviewRequest here now contains data from the new article that was created
        this.args.onStatusChange(Status.Approved, {
          entityId: reviewRequest.get('entityId'),
          entityType: reviewRequest.get('entityType'),
        });
      } else {
        this.args.onStatusChange(Status.Approved);
      }
    } else {
      await taskFor(this.suggestionService.rejectSuggestion).perform(reviewRequest);
      this.args.onStatusChange(Status.Rejected);
    }
  }

  get reviewEditsButtonLabel() {
    return this.intl.t(
      'knowledge-hub.content-editor.review.suggestion-summary-card.review-button',
      {
        numberOfEdits: this.args.pendingEditsCount,
      },
    );
  }

  get contradictingReviewReason() {
    let reviewReason = this.args.reviewRequest?.reviewReason;
    if (!reviewReason) {
      return this.intl.t(
        'knowledge-hub.content-editor.side-by-side-review.fallback-contradicting-reason',
      );
    }

    // Check if activeContent is a ContentSectionRelationship
    if (!this.isContentSectionRelationship(this.args.activeContent)) {
      return reviewReason;
    }

    // Get the source and target title if they exist. Fall back to 'Content A' and 'Content B'.
    let sourceTitle =
      this.args.activeContent.sourceSection?.relatedContent?.title ||
      this.intl.t('knowledge-hub.content-editor.side-by-side-review.fallback-content-source');
    let targetTitle =
      this.args.activeContent.targetSection?.relatedContent?.title ||
      this.intl.t('knowledge-hub.content-editor.side-by-side-review.fallback-content-target');

    let sourceSectionHash = this.args.activeContent.sourceSection?.sectionHash;
    let targetSectionHash = this.args.activeContent.targetSection?.sectionHash;

    return replaceContentPlaceholders(
      reviewReason,
      sourceTitle,
      targetTitle,
      sourceSectionHash,
      targetSectionHash,
    );
  }

  get duplicateReviewBaseReason() {
    // Check if activeContent is a ContentSectionRelationship
    if (!this.isContentSectionRelationship(this.args.activeContent)) {
      return this.intl.t(
        'knowledge-hub.content-editor.side-by-side-review.duplicate-review.reason-generic',
      );
    }

    let { sourceSection, targetSection } = this.args.activeContent;

    if (!sourceSection || !targetSection) {
      return this.intl.t(
        'knowledge-hub.content-editor.side-by-side-review.duplicate-review.reason-generic',
      );
    }

    return getDuplicateReviewBaseReason(sourceSection, targetSection, this.intl);
  }

  get duplicateReviewBulletPoints() {
    // Check if activeContent is a ContentSectionRelationship
    if (!this.isContentSectionRelationship(this.args.activeContent)) {
      return [];
    }

    let { sourceSection, targetSection } = this.args.activeContent;

    if (!sourceSection || !targetSection) {
      return [];
    }

    let sourceStats = this.args.getAiStatsForSection?.(sourceSection) ?? [];
    let targetStats = this.args.getAiStatsForSection?.(targetSection) ?? [];

    return getDuplicateReviewBulletPoints(
      sourceSection,
      targetSection,
      this.intl,
      sourceStats,
      targetStats,
    );
  }

  private isContentSectionRelationship(content: any): content is ContentSectionRelationship {
    return content && 'sourceSection' in content && 'targetSection' in content;
  }

  get summaryExplanation() {
    if (this.args.reviewRequest?.get('reviewType') === ReviewType.CREATE_CONTENT) {
      return this.args.reviewRequest?.get('reviewReason');
    }

    if (this.args.reviewRequest?.get('suggestion')?.seedContentExplanation) {
      return this.args.reviewRequest?.get('suggestion')?.seedContentExplanation;
    }

    return this.intl.t(
      'knowledge-hub.content-editor.review.suggestion-summary-card.summary-explanation-fallback',
    );
  }

  @action
  onCloseConversationSideBar() {
    this.showSidePanel = false;
  }

  @action
  goToFirstSuggestion() {
    this.args.onGoToFirstSuggestion();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::ContentReview::SuggestionSummaryCard': typeof SuggestionSummaryCard;
    'knowledge-hub/content-editor/content-review/suggestion-summary-card': typeof SuggestionSummaryCard;
  }
}
